// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import {
    HighAvailabilityEntityStatus,
    IHighAvailabilityActivity,
    IHighAvailabilityActivityEntity,
} from 'common/api/resources/HighAvailability';
import { Loader } from 'common/components';
import {
    Action,
    Translate,
    Button,
} from '@plesk/ui-library';
import List from 'common/components/List/List';
import { getActionColumnProps } from 'common/helpers/list';
import { pathTo } from 'common/helpers/core';
import { HIGH_AVAILABILITY_HISTORY } from 'admin/highAvailability/constants/test';
import { HighAvailabilityEntityStatusComponent } from 'admin/highAvailability/components/HistoryTable/HighAvailabilityEntityStatus';
import { formatTableDate } from 'common/date';
import { formatDuration } from 'common/helpers/duration';
import { ICONS } from 'common/constants';
import { TASK_STATUS } from 'common/api/resources/Task';
import {
    EmptyView,
    EntityRowList,
} from 'admin/highAvailability/components/HistoryTable/Styles';
import { COLUMN_WIDTHS } from 'admin/highAvailability/components/HistoryTable/columnWidths';
import { dataCySelector } from 'common/tests/selectors';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as highAvailabilityActions from 'common/modules/highAvailability/actions';
import InfiniteScroll from 'common/components/InfinityScroll/InfinityScroll';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { RootState } from 'admin/core/store';
import { Link } from 'react-router-dom';
import CopyText from 'common/containers/CopyText/CopyText';
import { COLORS } from 'common/theme';

interface IHighAvailabilityActivityEntityRowProps extends RouteComponentProps {
    activity: IHighAvailabilityActivity;
    isLoading: boolean;
    isActivitySelected: boolean;
    handleAddProcessesToSelection: (activity: IHighAvailabilityActivity, processes: IHighAvailabilityActivityEntity[]) => void;
    handleRemoveProcessesFromSelection: (activity: IHighAvailabilityActivity, processes: IHighAvailabilityActivityEntity[]) => void;
    onMoveBack: (processIds: number[]) => void;
}

export type HighAvailabilityEntityRowProps =
    IHighAvailabilityActivityEntityRowProps &
    ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

const columns = [{
    key: 'colId',
    width: COLUMN_WIDTHS.id,
    title: <Translate content="highAvailability.activities.processes.list.id" />,
}, {
    key: 'colServerName',
    width: COLUMN_WIDTHS.computeResource,
    title: <Translate content="highAvailability.activities.processes.list.serverName" />,
}, {
    key: 'colStatus',
    width: COLUMN_WIDTHS.status,
    title: <Translate content="highAvailability.activities.processes.list.status" />,
}, {
    key: 'colStartedAt',
    width: COLUMN_WIDTHS.startedAt,
    title: <Translate content="highAvailability.activities.processes.list.startedAt" />,
}, {
    key: 'colDuration',
    width: COLUMN_WIDTHS.duration,
    title: <Translate content="highAvailability.activities.processes.list.duration" />,
}, {
    key: 'colDestinationCr',
    width: COLUMN_WIDTHS.destinationCr,
    title: <Translate content="highAvailability.activities.processes.list.destinationCr" />,
}, {
    key: 'colFailoverDomain',
    width: COLUMN_WIDTHS.failoverDomain,
    title: <Translate content="highAvailability.activities.processes.list.failoverDomain" />,
}, getActionColumnProps({ width: COLUMN_WIDTHS.actions }),
];

export const HighAvailabilityEntityRow: React.FC<HighAvailabilityEntityRowProps> = ({
    activity,
    isLoading,
    isActivitySelected,
    handleAddProcessesToSelection,
    handleRemoveProcessesFromSelection,
    onMoveBack,
    haActions: {
        getHighAvailabilityEntitiesForActivity,
        loadMoreHighAvailabilityEntitiesOnScroll,
    },
}) => {
    const [selection, setSelection] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (!activity.entities && !activity.is_loading) {
            getHighAvailabilityEntitiesForActivity(activity.id);
        }
    }, [activity.id, activity.entities, activity.is_loading]); // eslint-disable-line react-hooks/exhaustive-deps

    // Stable identity of the loaded entity set so the effect re-runs when
    // infinite scroll appends a new page. Without this, processes loaded
    // AFTER 'select all' were never added to the parent selection — the
    // admin saw "all selected" but Move Back acted only on page 1.
    const processesKey = (activity.entities?.data ?? []).map((p) => p.id).join(',');
    React.useEffect(() => {
        const processes = (activity.entities?.data ?? []);
        if (processes.length === 0) {
            return;
        }

        if (isActivitySelected) {
            // Re-add on every entities change (covers initial
            // select-all AND newly appended pages from infinite scroll).
            handleAddProcessesToSelection(activity, processes);
            setSelection(processes.map((p) => p.id.toString()));
        } else if (processes.every((p) => selection.includes(p.id.toString()))) {
            // Activity was deselected — drop the previously-selected processes.
            handleRemoveProcessesFromSelection(activity, processes);
            setSelection([]);
        }

    }, [isActivitySelected, isLoading, processesKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleProcessSelect = (ids: string[]) => {
        const processes = (activity.entities?.data ?? []);
        if (ids.length >= selection.length) {
            const newlyAddedIds = ids.filter((id: string) => !selection.includes(id));
            handleAddProcessesToSelection(
                activity,
                processes.filter(p => newlyAddedIds.includes(p.id.toString()))
            );
        } else {
            const removedIds = selection.filter((id: string) => !ids.includes(id));
            handleRemoveProcessesFromSelection(
                activity,
                processes.filter(p => removedIds.includes(p.id.toString()))
            );
        }

        setSelection(ids);
    };

    const processesData = activity.entities?.data;

    const list = React.useMemo(() => (processesData ?? []).map((process: IHighAvailabilityActivityEntity) => {
        const isMoveBackFailed = process.move_back_task?.status === TASK_STATUS.FAILED;
        const isMoveBackTaskDone = process.move_back_task?.status === TASK_STATUS.DONE;
        const isMoveBackDone = isMoveBackTaskDone && process.is_migrated_back;
        const isMoveBackPending = process.move_back_task?.status === TASK_STATUS.PENDING;
        const isMoveBackInProgress = !!process.move_back_task && !process.is_migrated_back && !isMoveBackFailed && !isMoveBackTaskDone;
        const canMoveBackRow = process.status === HighAvailabilityEntityStatus.COMPLETED
            && !process.is_migrated_back
            && !isMoveBackInProgress
            && !process.is_move_back_pending;

        const moveBackTooltip = canMoveBackRow
            ? <Translate content="highAvailability.activities.operations.moveBackButton" />
            : isMoveBackInProgress
                ? <Translate content="highAvailability.activities.operations.moveBackDisabledMigrationInProgress" />
                : process.status === HighAvailabilityEntityStatus.FAILED
                    ? <Translate content="highAvailability.activities.operations.moveBackDisabledTooltip" />
                    : process.status !== HighAvailabilityEntityStatus.COMPLETED
                        ? <Translate content="highAvailability.activities.operations.moveBackRowDisabledNotCompleted" />
                        : process.is_migrated_back
                            ? <Translate content="highAvailability.activities.operations.moveBackCannotRevertTooltip" />
                            : <Translate content="highAvailability.activities.operations.moveBackRowDisabledNotCompleted" />;

        const actionEl = (
            <Button
                ghost={true}
                color={COLORS.GREY_80}
                data-cy={dataCySelector(process.id, HIGH_AVAILABILITY_HISTORY.ROW_MOVE_BACK_BUTTON)}
                disabled={!canMoveBackRow || !!process.is_loading}
                state={(process.is_loading || process.is_move_back_pending) ? 'loading' : undefined}
                icon={ICONS.ARROW_BACK}
                onClick={() => onMoveBack([process.id])}
                tooltip={moveBackTooltip}
            />
        );

        return {
            colId: process.id,
            colServerName: (
                <Action
                    component={Link}
                    to={pathTo(`servers/${process.compute_resource_vm.id}`)}
                >
                    {process.compute_resource_vm.name}
                </Action>
            ),
            colStatus: (
                <HighAvailabilityEntityStatusComponent
                    status={process.status}
                    progress={process.task?.progress}
                    moveBackProgress={isMoveBackInProgress && !isMoveBackPending ? process.move_back_task?.progress : undefined}
                    isMoveBackPending={isMoveBackPending}
                    isMoveBackFailed={isMoveBackFailed}
                    isMoveBackDone={isMoveBackDone}
                    failReason={process.fail_reason}
                    compact={true}
                />
            ),
            colStartedAt: (
                <>
                    {process.started_at
                        ? <CopyText>{formatTableDate(process.started_at)}</CopyText>
                        : <>&mdash;</>}
                    {process.ended_at && <CopyText>{formatTableDate(process.ended_at)}</CopyText>}
                </>
            ),
            colDuration: formatDuration(process.started_at, process.ended_at),
            colDestinationCr: (
                process.destination_compute_resource
                    ? (<Action
                        component={Link}
                        to={pathTo(`compute_resources/${process.destination_compute_resource.id}`)}
                    >
                        {process.destination_compute_resource.name}
                    </Action>)
                    : <>&mdash;</>
            ),
            colFailoverDomain: process.high_availability.failover_domain
                ? (
                    <Action
                        component={Link}
                        to={pathTo(`failover_domains/${process.high_availability.failover_domain.id}/activities`)}
                    >
                        {process.high_availability.failover_domain.name}
                    </Action>
                )
                : <>&mdash;</>,
            colActions: actionEl,
            key: process.id.toString(),
        };
    }), [processesData, onMoveBack]);

    const loadMoreProcesses = () => loadMoreHighAvailabilityEntitiesOnScroll(activity);

    return (
        <InfiniteScroll
            loadMore={loadMoreProcesses}
            hasMore={!!activity.entities?.links.next}
        >
            <Loader isLoading={isLoading} center={false}>
                <EntityRowList>
                    <List
                        columns={columns}
                        data={list}
                        selection={selection}
                        onSelectionChange={handleProcessSelect}
                        emptyView={
                            <EmptyView>
                                <Translate content="highAvailability.activities.processes.list.empty" />
                            </EmptyView>
                        }
                    />
                </EntityRowList>
            </Loader>
        </InfiniteScroll>
    );
};

const mapStateToProps = (state: RootState) => ({
    isLoadingInfinityScroll: state.app.loadingFlags.has(LOADING_FLAGS.HIGH_AVAILABILITY_ENTITIES_INFINITY_SCROLL),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    haActions: bindActionCreators(highAvailabilityActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HighAvailabilityEntityRow));
